/**
 * 分项报价编辑页面
 * 作为独立的步骤，用于填写和计算分项报价
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import QuotationEditor, { QuotationData, QuotationItem } from '../components/QuotationEditor';
import { quotationApi, projectApi, documentApi } from '../services/api';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { CalculatorIcon, ArrowRightIcon, ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface QuotationEditProps {
  initialData?: QuotationData | null;
  onSave?: (data: QuotationData) => void;
  onNext?: () => void;
  onPrev?: () => void;
}

const defaultQuotationData: QuotationData = {
  items: [],
  maxTotalPrice: null,
  warningPercent: 90,
  notes: ''
};

const QuotationEdit: React.FC<QuotationEditProps> = ({
  initialData,
  onSave,
  onNext,
  onPrev
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [quotationData, setQuotationData] = useState<QuotationData>(initialData || defaultQuotationData);
  const [quotationId, setQuotationId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [extracting, setExtracting] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>(''); // 项目配置的模型
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({ show: false, type: 'info', title: '' });
  
  // 确认对话框状态
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  
  // 防抖定时器引用
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveRef = useRef<(() => Promise<void>) | null>(null);  // 供卸载时兜底保存使用
  const lastSavedDataRef = useRef<string>('');
  const hasLoadedOnceRef = useRef<boolean>(false); // 是否已经加载过一次

  // 从后端加载报价数据
  const loadSavedQuotationData = useCallback(async () => {
    console.log('开始加载报价数据, projectId:', projectId);
    if (!projectId) {
      console.log('跳过加载: projectId为空');
      return;
    }
      
    setLoading(true);
    try {
      // 并行加载项目信息和报价数据
      const [projectResponse, quotationResponse] = await Promise.all([
        projectApi.getProject(projectId),
        quotationApi.getLatest(projectId)
      ]);
      
      // 设置项目模型
      if (projectResponse.data?.selected_model) {
        setSelectedModel(projectResponse.data.selected_model);
      }
      
      // 获取项目最新报价
      if (quotationResponse.data) {
        const data = quotationResponse.data;
        setQuotationId(data.id);
        // 按 sort_order 排序后加载
        const sortedItems = data.items?.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)) || [];
        const loadedData = {
          items: sortedItems.map((item: any) => ({
            id: item.id?.toString() || Date.now().toString() + Math.random().toString(36).substr(2, 9),
            category: item.category || '',
            name: item.name,
            description: item.description || '',
            unit: item.unit,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            isRequired: item.is_required
          })),
          maxTotalPrice: data.max_total_price,
          warningPercent: data.warning_percent || 90,
          notes: data.notes || ''
        };
        setQuotationData(loadedData);
        // 同步更新 ref，确保自动保存比较时数据一致
        quotationDataRef.current = loadedData;
        // 记录最后保存的数据（在设置 state 之前，避免 useEffect 中比较时数据不一致）
        lastSavedDataRef.current = JSON.stringify(loadedData);
        hasLoadedOnceRef.current = true;
        return data.id; // 返回quotationId
      }
    } catch (error) {
      // 注意：加载失败（网络/401/超时等）时**绝不能**自动重新提取——
      // extractFromAnalysis 会用解析结果覆盖当前报价（单价为空），
      // 等于把用户已填好的单价清空。保留页面上已有的数据，让用户刷新重试即可。
      console.error('加载报价数据失败（保留当前数据，不自动重新提取）:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 加载已保存的报价数据 - projectId 变化或重新进入本步骤时触发。
  // 说明：这里不再用"1 秒内跳过"的模块级时间戳做去重——组件重新挂载时（例如从
  // 招标解析返回本步骤）会被误判为重复加载而直接跳过，导致页面停留在空数据上。
  // StrictMode 下最多多发一次 GET，无副作用。
  useEffect(() => {
    if (!projectId) return;
    loadSavedQuotationData();
  }, [projectId]);

  // 自动保存：数据变化时触发防抖保存
  useEffect(() => {
    if (!projectId || loading) return;
    
    // 如果还没有加载过数据，不触发自动保存
    if (!hasLoadedOnceRef.current) return;
    
    // 使用当前 state 中的 quotationData，而不是 ref
    // 因为此 useEffect 依赖于 quotationData，它是最新的
    const currentDataStr = JSON.stringify(quotationData);
    
    // 如果数据没有变化，不保存
    if (currentDataStr === lastSavedDataRef.current) {
      return;
    }
    
    // 更新 ref 为最新值，供 autoSave 函数使用
    quotationDataRef.current = quotationData;
    
    // 清除之前的定时器
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // 设置保存状态为"保存中"
    setAutoSaveStatus('saving');
    
    // 防抖保存：延迟1.5秒执行
    autoSaveTimerRef.current = setTimeout(() => {
      autoSave();
    }, 1500);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [quotationData, projectId, loading]);

  // 用 ref 存储最新的 quotationData，供卸载时使用
  const quotationDataRef = useRef(quotationData);
  useEffect(() => {
    quotationDataRef.current = quotationData;
  }, [quotationData]);

  // 组件卸载（如点击左侧步骤切换）时：若还有未落库的改动，兜底保存一次再离开
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
        if (projectId && hasLoadedOnceRef.current
            && JSON.stringify(quotationDataRef.current) !== lastSavedDataRef.current) {
          // 卸载后 state 不再更新，直接发起保存请求（fire-and-forget）
          void autoSaveRef.current?.();
        }
      }
      // 重置加载状态，允许下次进入时重新加载
      hasLoadedOnceRef.current = false;
    };
  }, [projectId]);

  // 自动保存函数
  const autoSave = useCallback(async () => {
    if (!projectId || loading) return; // 加载中不保存
    
    // 使用 ref 获取最新数据
    const currentData = quotationDataRef.current;
    
    try {
      const payload = {
        project_id: projectId,
        version: 1,
        max_total_price: currentData.maxTotalPrice,
        warning_percent: currentData.warningPercent,
        notes: currentData.notes,
        items: currentData.items.map((item, idx) => ({
          category: item.category || '',
          name: item.name,
          description: item.description,
          unit: item.unit,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          is_required: item.isRequired,
          sort_order: idx
        }))
      };

      if (quotationId) {
        // 已有ID，直接更新
        await quotationApi.update(quotationId, payload);
      } else {
        // 没有ID，尝试创建
        try {
          const response = await quotationApi.create(payload);
          setQuotationId(response.data.id);
        } catch (createError: any) {
          // 如果创建失败且是版本已存在错误，重新加载获取ID
          if (createError?.response?.status === 400 && createError?.response?.data?.detail?.includes('已存在')) {
            console.log('版本已存在，重新加载并更新...');
            // loadSavedQuotationData 会返回 quotationId 并设置状态
            const loadedId = await loadSavedQuotationData();
            if (loadedId) {
              // 直接使用加载返回的ID更新
              await quotationApi.update(loadedId, payload);
            }
            return;
          }
          console.error('创建报价失败:', createError);
          throw createError;
        }
      }
      
      // 更新最后保存的数据
      lastSavedDataRef.current = JSON.stringify(currentData);
      setAutoSaveStatus('saved');
      
      // 3秒后恢复为idle状态
      setTimeout(() => {
        setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
      }, 3000);
      
      // 触发 onSave 回调
      onSave?.(currentData);
    } catch (error) {
      console.error('自动保存失败:', error);
      setAutoSaveStatus('idle');
    }
  }, [projectId, quotationId, loading, loadSavedQuotationData, onSave]);

  // 保持 autoSaveRef 指向最新的 autoSave（卸载时兜底保存用）
  useEffect(() => {
    autoSaveRef.current = autoSave;
  }, [autoSave]);

  // 立即保存尚未落库的改动（离开本步骤前调用，避免 1.5s 防抖窗口内切步骤导致丢失）
  const flushPendingSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (!projectId || loading || !hasLoadedOnceRef.current) return;
    if (JSON.stringify(quotationDataRef.current) === lastSavedDataRef.current) return;
    await autoSave();
  }, [projectId, loading, autoSave]);

  // 从招标解析结果智能提取报价项
  const extractFromAnalysis = async () => {
    if (!projectId) return;
    
    // 如果已有报价项，显示确认对话框
    if (quotationData.items.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: '确认重新提取',
        message: '重新提取将清空当前已生成的报价信息，\n确定重新提取吗？',
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doExtractFromAnalysis();
        }
      });
    } else {
      // 没有现有数据，直接提取
      doExtractFromAnalysis();
    }
  };
  
  // 执行智能提取
  const doExtractFromAnalysis = async () => {
    if (!projectId) return;
    setExtracting(true);
    try {
      // 并行获取项目信息和分项报价解析结果
      const [projectResponse, analysisResponse] = await Promise.all([
        projectApi.getProject(projectId),
        projectApi.getAnalysis(projectId, 'quotation')
      ]);
      
      // 获取项目模型
      const projectModel = projectResponse.data?.selected_model || selectedModel || 'gpt-4o-mini';
      console.log('使用模型:', projectModel);
      
      console.log('API响应:', analysisResponse.data);
      if (analysisResponse.data?.content) {
        console.log('Markdown内容:', analysisResponse.data.content);
        
        // 并行执行：解析Markdown + AI提取最高限价
        const [extracted, maxPriceResult] = await Promise.all([
          parseQuotationFromMarkdown(analysisResponse.data.content),
          documentApi.extractMaxPrice(analysisResponse.data.content, projectModel)
        ]);
        
        console.log('解析结果:', extracted);
        console.log('最高限价提取结果:', maxPriceResult.data);
        
        // 如果AI提取到最高限价，使用AI的结果
        if (maxPriceResult.data?.max_price) {
          extracted.maxTotalPrice = maxPriceResult.data.max_price;
          console.log('使用AI提取的最高限价:', extracted.maxTotalPrice, '原文:', maxPriceResult.data.original_text);
        }
        
        if (extracted.items.length > 0) {
          setQuotationData(extracted);
          // 智能提取完成后立即保存
          setTimeout(() => {
            autoSave();
          }, 100);
          showToast('success', '已智能提取报价项', [`共提取 ${extracted.items.length} 个分项${extracted.maxTotalPrice ? `，最高限价 ${(extracted.maxTotalPrice / 10000).toFixed(0)}万元` : ''}`]);
        } else {
          showToast('warning', '未提取到报价项', ['请检查招标解析的分项报价内容']);
        }
      } else {
        showToast('warning', '未找到招标解析数据', ['请先在招标解析步骤中分析分项报价']);
      }
    } catch (error) {
      console.error('提取报价数据失败:', error);
      const errorMsg = (error as any)?.response?.data?.detail || (error as Error)?.message || '';
      const isTokenError = errorMsg.includes('Token') &&
          (errorMsg.includes('欠费') || errorMsg.includes('不足') || errorMsg.includes('额度'));
      // Token 欠费错误不显示 Toast，会弹出全局欠费弹框
      if (!isTokenError) {
        showToast('error', '提取失败', ['请稍后重试']);
      }
    } finally {
      setExtracting(false);
    }
  };

  // 解析Markdown格式的报价内容为结构化数据
  const parseQuotationFromMarkdown = (markdown: string): QuotationData => {
    console.log('开始解析Markdown，长度:', markdown.length);
    const result: QuotationData = {
      items: [],
      maxTotalPrice: null,
      warningPercent: 90,
      notes: ''
    };

    // 提取最高限价 - 支持多种格式
    const maxPricePatterns = [
      // 匹配**最高限价**后面的内容，支持中间有其他文字
      /最高投标限价[\s\S]*?(\d+(?:\.\d+)?)\s*万?元/,
      /最高限价[\s\S]*?(\d+(?:\.\d+)?)\s*万?元/,
      /投标限价[\s\S]*?(\d+(?:\.\d+)?)\s*万?元/,
      // 简单格式：最高限价：XXX万元
      /最高投标限价[：:]\s*(\d+(?:\.\d+)?)\s*万?元?/,
      /最高限价[：:]\s*(\d+(?:\.\d+)?)\s*万?元?/,
      /投标限价[：:]\s*(\d+(?:\.\d+)?)\s*万?元?/,
      /预算[：:]\s*(\d+(?:\.\d+)?)\s*万?元?/
    ];
    
    for (const pattern of maxPricePatterns) {
      const match = markdown.match(pattern);
      if (match) {
        let price = parseFloat(match[1]);
        // 如果匹配文本中包含"万"字，需要乘以10000
        if (match[0].includes('万')) {
          price = price * 10000;
        }
        result.maxTotalPrice = price;
        console.log('提取到最高限价:', result.maxTotalPrice);
        break;
      }
    }

    // 提取表格中的分项
    const lines = markdown.split('\n');
    const seenItems = new Set<string>();
    let currentCategory = ''; // 当前阶段/类别
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // 检测阶段标题（如 ### 一、需求分析阶段）
      const categoryMatch = line.match(/^###\s*[一二三四五六七八九十]+[、.]\s*(.+)$/);
      if (categoryMatch) {
        currentCategory = categoryMatch[1].trim();
        console.log('检测到阶段:', currentCategory);
        continue;
      }
      
      // 跳过分隔行和表头行
      if (line.includes('---') || line.includes('序号') || !line.trim().startsWith('|')) {
        continue;
      }
      
      // 解析表格行（改进：处理内容中可能包含 | 的情况）
      // 首先移除行首尾的 | ，然后按 | 分割
      const trimmedLine = line.trim();
      if (!trimmedLine.startsWith('|') || !trimmedLine.endsWith('|')) {
        continue;
      }
      
      // 移除首尾的 | 后分割
      const content = trimmedLine.slice(1, -1);
      const cells = content.split('|').map(cell => cell.trim());
      
      console.log(`第${i}行，单元格数:`, cells.length, cells);
      
      // 表格列应该是：序号 | 分项名称 | 工作内容/规格 | 单位 | 数量 | 备注
      // 我们需要至少6列，但备注可能为空
      if (cells.length >= 5) {
        const seq = cells[0];
        const name = cells[1];
        const desc = cells[2];
        const unit = cells[3];
        const quantity = cells[4];
        
        // 验证是否是数据行（序号应该是数字）
        if (!/^\d+$/.test(seq)) {
          console.log(`第${i}行序号不是数字，跳过:`, seq);
          continue;
        }
        
        const itemKey = name.trim();
        if (seenItems.has(itemKey)) continue;
        seenItems.add(itemKey);
        
        const item: QuotationItem = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          category: currentCategory,
          name: name.trim(),
          description: desc ? desc.trim() : '',
          unit: unit ? unit.trim() : '项',
          quantity: parseFloat(quantity) || 1,
          unitPrice: null,
          isRequired: true,
          sortOrder: parseInt(seq)
        };
        
        console.log('提取到分项:', item);
        result.items.push(item);
      }
    }

    console.log('解析完成，共提取:', result.items.length, '个分项');
    return result;
  };

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // 计算总价
  const totalPrice = quotationData.items.reduce((sum, item) => {
    return sum + (item.quantity * (item.unitPrice || 0));
  }, 0);

  // 检查是否超支
  const isOverBudget = !!(quotationData.maxTotalPrice && totalPrice > quotationData.maxTotalPrice);

  // 检查是否可以进入下一步
  const canGoNext = (): { ok: boolean; reason?: string } => {
    if (!quotationData.maxTotalPrice || quotationData.maxTotalPrice <= 0) {
      return { ok: false, reason: '请先填写最高限价' };
    }
    if (quotationData.items.length === 0) {
      return { ok: false, reason: '请先添加分项报价条目' };
    }
    const invalidItems = quotationData.items.filter(
      item => !item.unit?.trim() || !(item.quantity > 0) || !(item.unitPrice !== null && item.unitPrice > 0)
    );
    if (invalidItems.length > 0) {
      return { ok: false, reason: `${invalidItems.length}个分项信息不完整（单位、数量、单价不能为空）` };
    }
    if (isOverBudget) {
      return { ok: false, reason: '报价总价已超出最高限价' };
    }
    return { ok: true };
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={5000}
      />

      {/* 页面标题 */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <CalculatorIcon className="w-7 h-7 mr-3" />
              分项报价
            </h1>
            <p className="text-blue-100">
              基于第二步：【招标解析】中的分项报价信息，进行生成和检查
            </p>
          </div>
          {/* 智能提取按钮 - 始终显示，可重新提取 */}
          <div className="flex items-center space-x-3">
            {/* 加载状态 */}
            {loading && (
              <span className="text-sm text-blue-100 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                加载中...
              </span>
            )}
            {/* 自动保存状态 */}
            {!loading && (
              <span className="text-sm text-blue-100">
                {autoSaveStatus === 'saving' && '保存中...'}
                {autoSaveStatus === 'saved' && '✓ 已自动保存'}
                {autoSaveStatus === 'idle' && ''}
              </span>
            )}
            <button
              onClick={extractFromAnalysis}
              disabled={extracting || loading}
              className="flex items-center px-5 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium backdrop-blur-sm"
              title="从招标解析结果中重新提取报价项"
            >
              {extracting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  提取中...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-5 h-5 mr-2" />
                  智能提取
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 报价编辑器 */}
      <QuotationEditor
        initialData={quotationData}
        onChange={setQuotationData}
      />

      {/* 操作按钮 */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <button
          onClick={async () => { await flushPendingSave(); onPrev?.(); }}
          className="flex items-center px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          上一步：招标解析
        </button>

        <div className="flex items-center space-x-4">
          {isOverBudget && (
            <span className="text-red-600 font-medium">
              ⚠️ 已超出预算限制
            </span>
          )}
          <button
            onClick={async () => {
              const check = canGoNext();
              if (!check.ok) {
                setToast({ show: true, type: 'warning', title: '请先完善分项报价', messages: [check.reason || ''] });
                return;
              }
              await flushPendingSave();  // 先落库再离开，避免防抖窗口内切步骤丢数据
              onNext?.();
            }}
            disabled={false}
            className={`flex items-center px-6 py-2.5 rounded-lg transition-colors font-medium ${
              !canGoNext().ok
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            下一步：目录生成
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="确定"
        cancelText="取消"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default QuotationEdit;